/**
 * Recently viewed products.
 *
 * Handles are held in the shopify_recently_viewed cookie, as on the live theme.
 * Cards are built from /products/{handle}.js, a core Shopify endpoint that
 * needs no section, template or theme plumbing to resolve. Earlier versions
 * fetched a rendered section, which added a dependency that could fail
 * silently and take the whole row with it.
 */

const COOKIE_NAME = 'shopify_recently_viewed';
const DAYS_TO_EXPIRE = 90;
const HOW_MANY_TO_STORE = 10;

function expiryDate() {
  const expires = new Date();
  expires.setTime(expires.getTime() + 3600000 * 24 * DAYS_TO_EXPIRE);
  return expires.toUTCString();
}

const cookie = {
  read() {
    // Duplicates of this cookie can exist while an older host-only or
    // domain-scoped copy is still present. Every copy is merged, most recent
    // first, rather than picking one and hoping. Writing normalises them back
    // to a single cookie.
    const lists = document.cookie
      .split('; ')
      .filter((row) => row.startsWith(`${COOKIE_NAME}=`))
      .map((row) => row.split('=').slice(1).join('='))
      .filter(Boolean)
      .map((value) => decodeURIComponent(value).split(' ').filter(Boolean));

    const merged = [];
    lists.forEach((list) => {
      list.forEach((handle) => {
        if (!merged.includes(handle)) merged.push(handle);
      });
    });

    return merged;
  },

  write(handles) {
    const value = encodeURIComponent(handles.join(' '));
    const secure = window.location.protocol === 'https:' ? '; secure; samesite=none' : '';

    // A host-only cookie and a domain cookie of the same name are separate
    // entries, and reads can pick up the wrong one. Any host-only duplicate is
    // expired first, then the domain-scoped cookie is written exactly as the
    // live theme writes it, so there is only ever one.
    document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;

    document.cookie =
      `${COOKIE_NAME}=${value}; expires=${expiryDate()}; path=/; ` +
      `domain=${window.location.hostname}${secure}`;
  },

  remove(handle) {
    const handles = this.read();
    const position = handles.indexOf(handle);
    if (position === -1) return;
    handles.splice(position, 1);
    this.write(handles);
  },
};

function currentProductHandle() {
  const path = decodeURIComponent(window.location.pathname);
  if (path.indexOf('/products/') === -1) return null;

  const handle = path.split('/products/')[1];
  if (!handle) return null;

  return handle.split('/')[0].split('?')[0];
}

function recordRecentlyViewed() {
  const handle = currentProductHandle();
  if (!handle) return;

  let handles = cookie.read();
  const position = handles.indexOf(handle);
  if (position !== -1) handles.splice(position, 1);

  handles.unshift(handle);
  handles = handles.slice(0, HOW_MANY_TO_STORE);
  cookie.write(handles);

  // Read straight back, so a cookie the browser refused to store is caught
  // here rather than looking like an empty history later.
  const readBack = cookie.read();
  if (readBack.length === 0) {
    console.warn('[recently-viewed] the cookie could not be written');
  }
}

function formatMoney(cents) {
  const currency = window.Shopify?.currency?.active || 'USD';
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency,
    }).format(cents / 100);
  } catch (error) {
    return `${(cents / 100).toFixed(2)}`;
  }
}

function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value ?? '';
  return div.innerHTML;
}

function buildCard(product) {
  const url = escapeHtml(product.url || `/products/${product.handle}`);
  const title = escapeHtml(product.title);
  const image = product.featured_image || product.images?.[0];
  const onSale =
    typeof product.compare_at_price === 'number' && product.compare_at_price > product.price;

  return `
    <div class="upsell-product${onSale ? ' upsell-product--sale' : ''}" data-upsell-id="${product.id}">
      <a class="upsell-product__image" href="${url}" tabindex="-1" aria-hidden="true">
        ${image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy" width="400" height="400">` : ''}
      </a>
      <div class="upsell-product__content">
        <a class="upsell-product__title" href="${url}">${title}</a>
        <p class="upsell-product__price">
          <span class="upsell-product__price-current">${formatMoney(product.price)}</span>
          ${onSale ? `<s class="upsell-product__price-was">${formatMoney(product.compare_at_price)}</s>` : ''}
        </p>
      </div>
    </div>
  `;
}

async function fetchProduct(handle) {
  const root = window.Shopify?.routes?.root || '/';
  const response = await fetch(`${root}products/${handle}.js`, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(`${response.status} for ${handle}`);
  return response.json();
}

/**
 * Debug panel.
 *
 * Appending ?rvdebug=1 to any product URL prints what the script found onto the
 * page itself, so the state can be read without opening the browser console.
 * It renders nothing unless the parameter is present.
 */
const DEBUG = new URLSearchParams(window.location.search).has('rvdebug');
const debugLines = [];

function debugLog(label, value) {
  if (!DEBUG) return;
  debugLines.push(`${label}: ${value}`);
}

function renderDebug(holder) {
  if (!DEBUG) return;

  const panel = document.createElement('pre');
  panel.setAttribute('data-recently-viewed-debug', '');
  panel.style.cssText =
    'grid-column:1/-1;white-space:pre-wrap;text-align:left;font-size:12px;line-height:1.5;' +
    'padding:12px;margin:0;border:1px solid #c00;background:#fff8f8;color:#333;';
  panel.textContent = ['RECENTLY VIEWED DEBUG', ...debugLines].join('\n');

  const root = holder.closest('[data-recently-viewed-root]') || holder;
  root.appendChild(panel);
  root.removeAttribute('hidden');
}

function collapse(holder) {
  if (DEBUG) {
    debugLog('outcome', 'would have hidden the section; kept visible for debug');
    return;
  }

  const root = holder.closest('[data-recently-viewed-root]');

  // Hidden, never removed. Deleting the markup made it impossible to tell a
  // section that had nothing to show from one that never rendered at all.
  if (holder.dataset.hideSection === 'true') {
    const section = root?.closest('.shopify-section') || root?.closest('[id^="shopify-section"]');
    if (section) {
      section.setAttribute('hidden', '');
      section.dataset.recentlyViewedState = 'empty';
      return;
    }
  }

  if (root) {
    root.setAttribute('hidden', '');
    root.dataset.recentlyViewedState = 'empty';
  }
}

async function renderRecentlyViewed(holder) {
  if (holder.dataset.recentlyViewedDone) return;
  holder.dataset.recentlyViewedDone = 'true';

  const limit = parseInt(holder.dataset.limit, 10) || 3;
  const currentHandle = currentProductHandle();
  const stored = cookie.read();
  const queue = stored.filter((handle) => handle !== currentHandle);

  debugLog('script running', 'yes');
  debugLog('write test', cookie.read().length > 0 ? 'cookie is readable' : 'COOKIE NOT WRITABLE');
  debugLog('raw cookie', document.cookie.includes(COOKIE_NAME) ? 'found' : 'NOT FOUND');
  debugLog('handles stored', stored.length ? stored.join(', ') : '(none)');
  debugLog('current product', currentHandle || '(not a product page)');
  debugLog('queue after excluding current', queue.length ? queue.join(', ') : '(empty)');
  debugLog('limit', limit);

  const attempted = queue.length;
  let shown = 0;
  let failed = 0;

  while (queue.length && shown < limit) {
    const handle = queue.shift();

    try {
      const product = await fetchProduct(handle);
      holder.insertAdjacentHTML('beforeend', buildCard(product));
      shown += 1;
      debugLog(`fetch ${handle}`, 'ok');
    } catch (error) {
      failed += 1;
      cookie.remove(handle);
      debugLog(`fetch ${handle}`, `FAILED, ${error.message}`);
    }
  }

  debugLog('cards shown', shown);

  if (shown > 0) {
    renderDebug(holder);
    return;
  }

  if (attempted > 0 && failed === attempted) {
    debugLog('outcome', 'every request failed; section left in place');
    console.warn('[recently-viewed] every product request failed; leaving the section in place');
    renderDebug(holder);
    return;
  }

  collapse(holder);
  renderDebug(holder);
}

function hydrate() {
  document.querySelectorAll('[data-recently-viewed]').forEach(renderRecentlyViewed);
}

recordRecentlyViewed();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', hydrate);
} else {
  hydrate();
}

document.addEventListener('shopify:section:load', hydrate);
